import { faTriangleExclamation } from '@fortawesome/pro-regular-svg-icons';
import { camelCase } from 'lodash';
import React from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { withIcon } from '../../../../core/components/Icon.tsx';
import Switch from '../../../../core/components/Switch.tsx';
import classnames from '../../../../core/utils/classnames.tsx';

const AgentSkillsFormFields = ({ form, availableSkills, availableToolNames }) => {
    const { t } = useTranslation('assistants', { keyPrefix: 'agentConfig' });

    return (
        <div className="flex flex-col gap-4">
            <Controller
                name="skillNames"
                control={form.control}
                render={({ field }) => (
                    <div className="flex flex-col gap-4">
                        {availableSkills
                            ?.toSorted((skillA, skillB) => {
                                const translationKeyA = camelCase(skillA?.key || '').replace('Skill', '');
                                const translationKeyB = camelCase(skillB?.key || '').replace('Skill', '');
                                const nameA = t(`skills.${translationKeyA}.name`);
                                const nameB = t(`skills.${translationKeyB}.name`);
                                return nameA.localeCompare(nameB);
                            })
                            ?.map((skill) => (
                                <SkillSwitchCard
                                    key={skill.key}
                                    skill={skill}
                                    availableToolNames={availableToolNames}
                                    isChecked={field.value.includes(skill.key)}
                                    onIsCheckedChange={(isChecked) => {
                                        const currentValues = form.getValues('skillNames') || [];
                                        if (isChecked) {
                                            form.setValue('skillNames', [...currentValues, skill.key]);
                                        } else {
                                            form.setValue(
                                                'skillNames',
                                                currentValues.filter((key) => key !== skill.key)
                                            );
                                        }
                                    }}
                                />
                            ))}
                    </div>
                )}
            />
        </div>
    );
};

export default AgentSkillsFormFields;

const WarningIcon = withIcon(faTriangleExclamation);

const SkillSwitchCard = ({
    isChecked,
    onIsCheckedChange,
    skill,
    availableToolNames,
    className,
    ...props
}: React.ComponentPropsWithRef<'div'> & {
    isChecked: boolean;
    onIsCheckedChange: (checked: boolean) => void;
    availableToolNames?: string[];
    skill: { name: string; key: string; requiredToolNames: string[] };
}) => {
    const { t } = useTranslation('assistants', { keyPrefix: 'agentConfig' });

    const translationKey = camelCase(skill?.key || '').replace('Skill', '');

    return (
        <div
            className={classnames(
                'rounded-xl flex flex-col gap-2 p-2 border border-line-weak border-solid bg-surface-weak shadow shadow-black/5 transition-colors',
                className
            )}
            {...props}
        >
            <label className="flex flex-col cursor-pointer hover:border-line-strong hover:bg-surface-medium group transition-colors rounded-md">
                <div className="flex justify-between items-center p-2 pb-0">
                    <h3 className="font-semibold text-content-strong">{t(`skills.${translationKey}.name`)}</h3>
                    <Switch value={isChecked} onValueChange={onIsCheckedChange} />
                </div>

                <div className="text-sm text-content-medium px-2 pb-2">{t(`skills.${translationKey}.description`)}</div>
            </label>
        </div>
    );
};
